import type { ReactNode } from 'react';
import type { DialerHost } from '../../embed/dialerState';
import { useComms } from './context';
import { DialerContext } from './dialer';
import { useHostDialer } from './useHostDialer';
import { useRcDialer } from './useRcDialer';

/**
 * Holds the module's RingCentral dialer — or the remote control for the
 * dashboard's, which is the same thing from every consumer's side.
 *
 * Mounted inside CommsProvider because both read the channel list from it, and
 * above the views so the phone stays registered across a tab change — a phone
 * that unregisters when the agent leaves the call log is a phone that misses
 * the call it was there for. Which one runs is settled by the source and never
 * changes for its lifetime, so the split is two components rather than a
 * branch inside one: a hook cannot be called conditionally, and a frame torn
 * down and rebuilt on a re-render is a phone that unregisters mid-call.
 */
export function DialerProvider({ children }: { children: ReactNode }) {
  const { source } = useComms();
  return source.dialerHost ? (
    <HostedDialer host={source.dialerHost}>{children}</HostedDialer>
  ) : (
    <OwnDialer>{children}</OwnDialer>
  );
}

/** The module's own frame — the standalone console, and any older dashboard. */
function OwnDialer({ children }: { children: ReactNode }) {
  const dialer = useRcDialer();
  return <DialerContext.Provider value={dialer}>{children}</DialerContext.Provider>;
}

/** The dashboard's, held in a frame that outlives this module (see useHostDialer). */
function HostedDialer({ host, children }: { host: DialerHost; children: ReactNode }) {
  const dialer = useHostDialer(host);
  return <DialerContext.Provider value={dialer}>{children}</DialerContext.Provider>;
}
